import React, { useContext, useState, useEffect } from "react"

import Paper from "@material-ui/core/Paper"
import Container from "@material-ui/core/Container"
import Grid from "@material-ui/core/Grid"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { format, parseISO, isToday } from "date-fns";

import useTickets from "../../hooks/useTickets"

import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	customFixedHeightPaper: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
		height: 120,
	},
	announcementPanel: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderLeft: `4px solid ${theme.palette.primary.main}`,
		backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e3f0ff",
	},
	announcementHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1),
	},
	announcementRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.5),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	announcementText: {
		flex: 1,
		fontSize: 14,
	},
	announcementMeta: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	unread: {
		borderLeft: `3px solid ${theme.palette.primary.main}`,
		paddingLeft: theme.spacing(1),
		backgroundColor: theme.palette.type === "dark" ? "rgba(25,118,210,0.1)" : "rgba(25,118,210,0.05)",
		borderRadius: 2,
	},
	read: {
		opacity: 0.6,
		paddingLeft: theme.spacing(1),
	},
	followUpPanel: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderLeft: `4px solid #f59e0b`,
		backgroundColor: theme.palette.type === "dark" ? "#2a1f0a" : "#fffbeb",
	},
	followUpRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.5),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	followUpTime: {
		fontSize: 12,
		fontWeight: 700,
		color: "#f59e0b",
		minWidth: 48,
	},
}))

const Dashboard = () => {
	const classes = useStyles()

	const { user } = useContext(AuthContext);
	const [announcements, setAnnouncements] = useState([]);
	const [showAllAnn, setShowAllAnn] = useState(false);
	const [followUps, setFollowUps] = useState([]);
	const [readIds, setReadIds] = useState(() => {
		try { return JSON.parse(localStorage.getItem("readAnnouncements") || "[]"); } catch { return []; }
	});

	useEffect(() => {
		api.get("/announcements").then(({ data }) => setAnnouncements(Array.isArray(data) ? data : [])).catch(() => {});
		api.get("/follow-ups").then(({ data }) => {
			try {
				const todayDue = (data || []).filter(fu => {
					try { return isToday(parseISO(fu.scheduledAt)); } catch { return false; }
				});
				setFollowUps(todayDue);
			} catch { setFollowUps([]); }
		}).catch(() => {});
	}, []);

	const markRead = (id) => {
		const next = [...readIds, id];
		setReadIds(next);
		localStorage.setItem("readAnnouncements", JSON.stringify(next));
	};

	const markAllRead = () => {
		const next = announcements.map(a => a.id);
		setReadIds(next);
		localStorage.setItem("readAnnouncements", JSON.stringify(next));
	};

	const isRead = (id) => readIds.includes(id);
	const unreadCount = announcements.filter(a => !isRead(a.id)).length;

	const displayedAnn = showAllAnn ? announcements : announcements.filter(a => !isRead(a.id));

	var userQueueIds = [];

	if (user.queues && user.queues.length > 0) {
		userQueueIds = user.queues.map(q => q.id);
	}

	const GetTickets = (status, showAll, withUnreadMessages) => {

		const { count } = useTickets({
			status: status,
			showAll: showAll,
			withUnreadMessages: withUnreadMessages,
			queueIds: JSON.stringify(userQueueIds)
		});
		return count;
	}

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				{announcements.length > 0 && (
					<Paper className={classes.announcementPanel} elevation={0}>
						<div className={classes.announcementHeader}>
							<Typography variant="subtitle2" style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
								<AnnouncementIcon fontSize="small" color="primary" />
								Comunicados
								{unreadCount > 0 && (
									<Chip label={`${unreadCount} nuevo${unreadCount > 1 ? "s" : ""}`} size="small" color="primary" style={{ height: 18, fontSize: 11 }} />
								)}
							</Typography>
							<div style={{ display: "flex", gap: 8 }}>
								{unreadCount > 0 && (
									<Chip label="Marcar todos leídos" size="small" onClick={markAllRead} clickable variant="outlined" />
								)}
								<Chip
									label={showAllAnn ? "Ver solo no leídos" : `Ver historial (${announcements.length})`}
									size="small"
									onClick={() => setShowAllAnn(v => !v)}
									clickable
									variant="outlined"
								/>
							</div>
						</div>
						<Divider style={{ marginBottom: 8 }} />
						{displayedAnn.length === 0 && (
							<Typography variant="body2" color="textSecondary" style={{ fontSize: 13, padding: "4px 0" }}>
								✓ Todos los comunicados han sido leídos.
							</Typography>
						)}
						{displayedAnn.map(a => (
							<div key={a.id} className={`${classes.announcementRow} ${isRead(a.id) ? classes.read : classes.unread}`}>
								<div style={{ flex: 1 }}>
									<Typography className={classes.announcementText}>{a.message}</Typography>
									{a.createdAt && (
										<Typography className={classes.announcementMeta}>
											{format(parseISO(a.createdAt), "dd/MM/yyyy HH:mm")}
										</Typography>
									)}
								</div>
								{!isRead(a.id) && (
									<Chip label="Leído" size="small" onClick={() => markRead(a.id)} clickable color="primary" variant="outlined" style={{ flexShrink: 0 }} />
								)}
								{isRead(a.id) && (
									<Typography variant="caption" color="textSecondary" style={{ flexShrink: 0 }}>✓ Leído</Typography>
								)}
							</div>
						))}
					</Paper>
				)}
				{followUps.length > 0 && (
					<Paper className={classes.followUpPanel} elevation={0}>
						<div className={classes.announcementHeader}>
							<Typography variant="subtitle2" style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
								<ScheduleIcon fontSize="small" style={{ color: "#f59e0b" }} />
								Seguimientos pendientes hoy
								<Chip label={followUps.length} size="small" style={{ height: 18, fontSize: 11, backgroundColor: "#f59e0b", color: "#fff" }} />
							</Typography>
						</div>
						<Divider style={{ marginBottom: 8 }} />
						{followUps.map(fu => (
							<div key={fu.id} className={classes.followUpRow}>
								<Typography className={classes.followUpTime}>
									{fu.scheduledAt ? format(parseISO(fu.scheduledAt), "HH:mm") : "--:--"}
								</Typography>
								<div style={{ flex: 1 }}>
									<Typography style={{ fontSize: 14, fontWeight: 600 }}>
										{fu.contact?.name || `Contacto #${fu.contactId}`}
									</Typography>
									{fu.note && (
										<Typography style={{ fontSize: 13, color: "inherit", opacity: 0.8 }}>{fu.note}</Typography>
									)}
								</div>
								{fu.notifyAI && (
									<Chip label="IA" size="small" style={{ height: 18, fontSize: 11, backgroundColor: "#6366f1", color: "#fff", flexShrink: 0 }} />
								)}
							</div>
						))}
					</Paper>
				)}
				<Grid container spacing={3}>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.inAttendance.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{GetTickets("open", "true", "false")}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.waiting.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{GetTickets("pending", "true", "false")}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
					<Grid item xs={4}>
						<Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
							<Typography component="h3" variant="h6" color="primary" paragraph>
								{i18n.t("dashboard.messages.closed.title")}
							</Typography>
							<Grid item>
								<Typography component="h1" variant="h4">
									{GetTickets("closed", "true", "false")}
								</Typography>
							</Grid>
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	)
}

export default Dashboard
